import React from "react";
import CcButton from "../../CoilConfigurator/components/CcButton";
import WarningBanner from "../components/WarningBanner";
import PipeGeometryTiles from "../components/PipeGeometryTiles";
import StepDownThresholdGauge from "../components/StepDownThresholdGauge";
import type {
  ConfigurationResult,
  EquipmentConfiguration,
  EquipmentKind,
} from "../models/types";

interface ConfigurationResultViewProps {
  result: ConfigurationResult;
  onBackToList: () => void;
  onViewEquipment: (kind: EquipmentKind) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onRecalculate?: () => void;
  recalculating?: boolean;
  deleting?: boolean;
}

const calcValue = (equipment: EquipmentConfiguration | null, label: string) =>
  equipment?.calculations.find((item) => item.label.toLowerCase().includes(label.toLowerCase()))
    ?.value ?? "—";

const dimensionValue = (equipment: EquipmentConfiguration | null, label: string) =>
  equipment?.keyDimensions.find((item) => item.label.toLowerCase().includes(label.toLowerCase()))
    ?.value ?? "—";

const equipmentField = (
  equipment: EquipmentConfiguration | null,
  labels: string[],
  fallback = "—",
) => {
  if (!equipment) return fallback;
  for (const label of labels) {
    const fromCalc = calcValue(equipment, label);
    if (fromCalc !== "—") return fromCalc;
    const fromDim = dimensionValue(equipment, label);
    if (fromDim !== "—") return fromDim;
  }
  return fallback;
};

const selectedOptionMatching = (equipment: EquipmentConfiguration | null, needle: string) => {
  if (!equipment) return "—";
  const match = equipment.options.find(
    (option) =>
      option.status === "Selected" && option.name.toLowerCase().includes(needle.toLowerCase()),
  );
  return match?.name ?? "—";
};

const blastFrameRows = (equipment: EquipmentConfiguration): [string, string][] => [
  [
    "Frame Size",
    equipmentField(equipment, ["frame size", "frame"], equipment.diameterCategory),
  ],
  [
    "Version",
    equipmentField(
      equipment,
      ["version"],
      equipment.lengthCategory ? `V${equipment.lengthCategory}` : "—",
    ),
  ],
  ["Blast Head Size", equipmentField(equipment, ["blast head", "head size"])],
  [
    "Carriage / Kite Type",
    (() => {
      const fromField = equipmentField(equipment, ["carriage", "kite"]);
      if (fromField !== "—") return fromField;
      return selectedOptionMatching(equipment, "kite");
    })(),
  ],
  ["CWC Config?", equipmentField(equipment, ["cwc"])],
];

const inductionCoilRows = (equipment: EquipmentConfiguration): [string, string][] => [
  [
    "Frame Size",
    equipmentField(equipment, ["frame size", "frame", "coil"], equipment.diameterCategory),
  ],
  ["Bottom End Version", equipmentField(equipment, ["bottom end", "bottom"])],
  ["CWC Config?", equipmentField(equipment, ["cwc"])],
  ["Step-Down Config?", equipmentField(equipment, ["step-down", "step down"])],
  [
    "Rule Version",
    equipmentField(equipment, ["rule version"], equipment.ruleVersion || "—"),
  ],
];

const SpecRow = ({ label, value }: { label: string; value: string }) => (
  <div className="op-spec-row">
    <span className="op-spec-row__k">{label}</span>
    <span className={`op-spec-row__v${value === "—" ? " op-spec-row__v--dash" : ""}`}>{value}</span>
  </div>
);

const EquipmentSpecCard = ({
  title,
  partNo,
  rows,
  onViewDetails,
}: {
  title: string;
  partNo: string;
  rows: [string, string][];
  onViewDetails: () => void;
}) => (
  <article className="op-spec-card">
    <header className="op-spec-card__head">
      <h3>{title}</h3>
      <span className="op-spec-card__part">{partNo}</span>
    </header>
    <div className="op-spec-card__body">
      {rows.map(([label, value]) => (
        <SpecRow key={label} label={label} value={value} />
      ))}
    </div>
    <div className="op-spec-card__actions">
      <CcButton variant="secondary" onClick={onViewDetails}>
        View Details
      </CcButton>
    </div>
  </article>
);

const ConfigurationResultView = ({
  result,
  onBackToList,
  onViewEquipment,
  onEdit,
  onDelete,
  onRecalculate,
  recalculating = false,
  deleting = false,
}: ConfigurationResultViewProps) => {
  const steelOd = result.pipe.outsideDiameter
    ? `${result.pipe.outsideDiameter} ${result.pipe.outsideDiameterUnit || "mm"}`
    : "—";
  const coating = result.process.coatingThickness
    ? `${result.process.coatingThickness} ${result.process.coatingThicknessUnit || "mm"}`
    : "—";
  const actionsBusy = recalculating || deleting;

  return (
    <div className="op-result">
      <CcButton variant="link" className="op-back-link" onClick={onBackToList}>
        ← All Configurations
      </CcButton>

      <div className="op-detail-head">
        <div className="op-detail-head__left">
          <div>
            <h2 className="op-section-title">
              {result.project.projectNumber} · {result.project.projectName || result.configurationId}
            </h2>
            <p className="op-muted">
              {result.project.customer || "—"} · {steelOd} steel pipe
              {coating !== "—" ? `, ${coating} coating` : ""}
            </p>
            <p className="op-muted">
              {result.configurationId} · Revision {result.revision}
              {result.versions
                ? ` · Dataset ${result.versions.datasetRevision} · BF ${result.versions.blastFrameRuleVersion} · IC ${result.versions.inductionCoilRuleVersion}`
                : ""}
            </p>
          </div>
          {onEdit || onDelete || onRecalculate ? (
            <div className="op-detail-head__actions">
              {onEdit ? (
                <CcButton variant="secondary" onClick={onEdit} disabled={actionsBusy}>
                  Edit
                </CcButton>
              ) : null}
              {onDelete ? (
                <CcButton variant="danger" onClick={onDelete} disabled={actionsBusy}>
                  {deleting ? "Deleting…" : "Delete"}
                </CcButton>
              ) : null}
              {onRecalculate ? (
                <CcButton variant="primary" onClick={onRecalculate} disabled={actionsBusy}>
                  {recalculating ? "Recalculating…" : "Recalculate"}
                </CcButton>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {(result.warnings.length > 0 || result.errorMessage) && (
        <div className="op-advisory-grid">
          <div className="op-advisory-card op-advisory-card--causes">
            <h3>Advisory Causes</h3>
            {result.warnings.length === 0 && !result.errorMessage ? (
              <p className="op-muted">No advisory conditions triggered.</p>
            ) : (
              <>
                {result.errorMessage ? <p>{result.errorMessage}</p> : null}
                {result.warnings.map((warning) => (
                  <WarningBanner key={`${warning.title}-${warning.message}`} warning={warning} />
                ))}
              </>
            )}
          </div>
          <div className="op-advisory-card op-advisory-card--actions">
            <h3>Advisory Actions</h3>
            <p className="op-muted">
              Review equipment details below, or adjust pipe/process inputs and regenerate if the fit
              is not acceptable.
            </p>
          </div>
        </div>
      )}

      <p className="op-section-label">Selected Equipment</p>
      {result.errorMessage && !result.blastFrame && !result.inductionCoil ? (
        <p className="op-field-error" role="alert">
          {result.errorMessage}
        </p>
      ) : (
        <div className="op-equip-grid op-equip-grid--detail">
          {result.blastFrame ? (
            <EquipmentSpecCard
              title="Manual Blast Frame"
              partNo={result.blastFrame.configurationId}
              rows={blastFrameRows(result.blastFrame)}
              onViewDetails={() => onViewEquipment("blast-frame")}
            />
          ) : null}
          {result.inductionCoil ? (
            <EquipmentSpecCard
              title="Induction Heat Coil"
              partNo={result.inductionCoil.configurationId}
              rows={inductionCoilRows(result.inductionCoil)}
              onViewDetails={() => onViewEquipment("induction-coil")}
            />
          ) : null}
        </div>
      )}

      <PipeGeometryTiles
        pipe={result.pipe}
        process={result.process}
        inputSummary={result.inputSummary}
      />

      <StepDownThresholdGauge
        pipe={result.pipe}
        process={result.process}
        inputSummary={result.inputSummary}
      />
    </div>
  );
};

export default ConfigurationResultView;
